import { useContext, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from '@/utils/i18n/useTranslation';

import Button from '@/components/Button/Button';
import { UserContext } from '@/context/UserContext';
import type { FormFieldConfig, FormItemEnabledState } from '@/types/domain';

interface FormDialogProps {
  dialogEvent: ChangeEvent<HTMLSelectElement> | false;
  item: FormFieldConfig;
  enabledState?: FormItemEnabledState[];
  onFormDialogConfirm: () => void;
  onFormDialogDecline: () => void;
}

const createMarkup = (copy: string) => ({ __html: copy });

const FormDialog = (props: FormDialogProps) => {
  const {
    dialogEvent,
    item,
    enabledState,
    onFormDialogConfirm,
    onFormDialogDecline,
  } = props;

  const userContext = useContext(UserContext);

  /* eslint-disable @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove */
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT,
  );
  /* eslint-enable @typescript-eslint/no-unnecessary-condition */

  const [bodyState, setBodyState] = useState<string | false>(false);
  const [relatedState, setRelatedState] = useState<
    FormItemEnabledState[] | false
  >(false);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
  const content = item.dialog!;

  useEffect(() => {
    if (!dialogEvent) return;

    const value =
      dialogEvent.target.options[
        dialogEvent.target.selectedIndex
      ].text.toLowerCase();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    const bodyString = t(content.body!).replace('[VALUE]', () => value);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs state from an external source (RHF/cookie/DOM/context/async data), which is not render-derivable
    if (bodyString) setBodyState(bodyString);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    const enabledItems = enabledState!.filter(
      (input) => input.enabled && !input.toEmpty && input.name !== item.name,
    );
    if (enabledItems.length > 0) {
      setRelatedState(enabledItems);
    } else {
      onFormDialogConfirm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- modal/form-dialog effect; intentional trigger (adding the recreated context callbacks would loop)
  }, [dialogEvent]);

  return (
    <div className="FormDialog">
      {content.heading && (
        <h4 className="FormDialog-heading">{t(content.heading)}</h4>
      )}
      {(bodyState || content.prompt) && (
        <div className="FormDialog-body">
          {bodyState && <p dangerouslySetInnerHTML={createMarkup(bodyState)} />}
          {relatedState && (
            <ul>
              {relatedState.map((item, index) => {
                return (
                  <li key={`relatedItem--${String(index)}`}>
                    {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */}
                    {t(item.label!)}
                  </li>
                );
              })}
            </ul>
          )}
          {content.prompt && (
            <p>
              <strong>{t(content.prompt)}</strong>
            </p>
          )}
        </div>
      )}
      <div className="FormDialog-buttonGroup">
        {content.confirmLabel && (
          <Button
            label={t(content.confirmLabel)}
            modifiers={['Button--selectDialogConfirm']}
            onClick={() => {
              onFormDialogConfirm();
            }}
          />
        )}
        {content.declineLabel && (
          <Button
            label={t(content.declineLabel)}
            modifiers={['Button--selectDialogDecline']}
            onClick={() => {
              onFormDialogDecline();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default FormDialog;
